import React, { useEffect, useState } from 'react';

import { useTranslation } from 'react-i18next';

import { EmojiImage } from '@pkg/components';
import { LabelledInput, type RewardType } from '@pkg/components/Sections/Admin';
import {
    PrizeBottom,
    PrizeContent,
    PrizeContentLink,
    PrizeDetails,
    PrizeImg,
    PrizeWrapper,
} from '@pkg/components/Sections/Admin/Fantasy/Prizes/styles';
import { CustomIconWrapper, IconWrapper } from '@pkg/components/Sections/Admin/components/StyledComponents/styles';
import { Sizes } from '@pkg/components/Sections/Profile/Buttons/Button/types';
import IconButton from '@pkg/components/Sections/Profile/IconButton';
import { ReactComponent as CheckIcon } from '@pkg/components/Sections/Profile/icons/check.svg';
import { ReactComponent as EditIcon } from '@pkg/components/Sections/Profile/icons/edit.svg';
import { ReactComponent as TrashIcon } from '@pkg/components/Sections/Profile/icons/trash-bin.svg';

import { type Prize } from '@app/client/common/structs';

import { Typography, Wrapper } from '../../../../DesignSystem';

interface PrizeItemProps {
    rewardType?: RewardType;
    prize: Prize;
    currency?: string;
    canEdit?: boolean;
    onUpdate?: (toUpdate: Prize, newData: Prize) => void;
    onRemoveReward?: (prizeToRemove: Prize, type: RewardType) => void;
    compact?: boolean;
}
export const PrizeItem = ({
    rewardType = 'player',
    prize,
    canEdit = false,
    onRemoveReward,
    onUpdate,
    currency,
    compact = true,
}: PrizeItemProps) => {
    const [prizeData, setPrizeData] = useState(prize);
    const [isEditing, setIsEditing] = useState(!prize.reward);
    const [confirmDisabled, setConfirmDisabled] = useState(false);

    const { t } = useTranslation('admin_fantasy');
    function handleRemove() {
        onRemoveReward?.(prize, rewardType);
    }

    useEffect(() => {
        setConfirmDisabled(!prizeData.rank || !prizeData.reward);
    }, [prizeData.rank, prizeData.reward]);

    function handleConfirm() {
        onUpdate?.(prize, { ...prizeData, description: currency || '' });
        setIsEditing(false);
    }

    function handleInputValues(e) {
        const { name, value, type } = e.target;
        let postValue = value;
        if (type === 'number') {
            postValue = parseInt(value, 10);
        }
        if (name === 'reward') {
            // BE takes this as a string
            postValue = postValue.toString();
        }

        setPrizeData((curr) => ({ ...curr, [name]: postValue }));
    }

    const prizeView = (
        <Wrapper direction="row" gap="10px" alignItems="center" padding="0 0 0 5px">
            {prizeData.image_url ? (
                <PrizeImg src={prizeData.image_url} alt={prizeData.name} />
            ) : (
                <EmojiImage sizeInPixels={30} emojiId={'🤷🏻'} alt="No Image" />
            )}
            <PrizeDetails>
                <PrizeBottom>
                    <Typography type="label" size={16}>
                        {`#${prizeData.rank}`}
                    </Typography>
                    <Typography type="label" size={16}>
                        {currency || prizeData.description} - {prizeData.reward}
                    </Typography>
                </PrizeBottom>
            </PrizeDetails>
        </Wrapper>
    );

    const editView = (
        <Wrapper flexWrap="wrap" direction="row" justifyContent="space-between" padding="0 10px">
            <LabelledInput
                label="Rank"
                required
                onChange={handleInputValues}
                name="rank"
                id="rank"
                value={prizeData.rank}
                placeholder={t('prizes_placeholder_rank')}
                type="number"
                min={1}
            />

            <LabelledInput
                onChange={handleInputValues}
                label="Reward value"
                placeholder="Reward prize"
                name="reward"
                required
                id="reward"
                value={prizeData.reward}
                type="number"
            />

            <LabelledInput
                label="Image Url"
                onChange={handleInputValues}
                name="image_url"
                id="image_url"
                value={prizeData.image_url ?? ''}
                placeholder={t('prizes_label_image')}
                type="url"
            />
            <LabelledInput
                label="Link Url"
                onChange={handleInputValues}
                name="link_url"
                id="link_url"
                value={prizeData.link_url ?? ''}
                placeholder={t('prizes_label_url')}
                type="url"
            />
        </Wrapper>
    );

    const prizeContent = (
        <PrizeContent $compact={compact}>
            <PrizeWrapper>
                {isEditing ? editView : prizeView}
                {canEdit ? (
                    isEditing ? (
                        <Wrapper direction="column" gap="8px" width="30px">
                            <IconButton onClick={handleConfirm} size={Sizes.small} disabled={confirmDisabled}>
                                <CustomIconWrapper>
                                    <CheckIcon />
                                </CustomIconWrapper>
                            </IconButton>
                            <IconButton onClick={handleRemove} size={Sizes.small}>
                                <IconWrapper>
                                    <TrashIcon />
                                </IconWrapper>
                            </IconButton>
                        </Wrapper>
                    ) : (
                        <IconButton onClick={() => setIsEditing(true)} size={Sizes.small}>
                            <CustomIconWrapper>
                                <EditIcon />
                            </CustomIconWrapper>
                        </IconButton>
                    )
                ) : null}
            </PrizeWrapper>
        </PrizeContent>
    );
    return (
        <PrizeWrapper>
            {prizeData.link_url && !canEdit ? (
                <PrizeContentLink $compact={compact} href={prizeData.link_url} target={'_blank'} rel="noreferrer">
                    {prizeContent}
                </PrizeContentLink>
            ) : (
                prizeContent
            )}
        </PrizeWrapper>
    );
};
